import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Heart, Leaf, ArrowRight } from "lucide-react";
import { setSeo, assetUrl } from "@/lib/api";
import { STORES } from "@/data/site";
import { useSite } from "@/context/SiteContext";

const IMG1 = "https://images.unsplash.com/photo-1762605135012-56a59a059e60?crop=entropy&cs=srgb&fm=jpg&q=90&w=1000";
const IMG2 = "https://images.unsplash.com/photo-1762605135376-ae5af70a5628?crop=entropy&cs=srgb&fm=jpg&q=90&w=1000";

export default function About() {
  const { settings } = useSite();
  useEffect(() => setSeo("About Us | Adam's The Islamic Boutique Chennai", "Adam's The Islamic Boutique — a premium destination for elegant Islamic modest fashion with stores in Chennai & Bengaluru."), []);

  return (
    <div>
      <section className="relative h-[46vh] min-h-[340px] overflow-hidden">
        <img src={IMG2} alt="Adam's boutique" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 to-espresso/20" />
        <div className="relative h-full flex items-end">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8 w-full pb-12">
            <p className="text-champagne-light overline mb-3">Our Story</p>
            <h1 className="font-serif text-5xl lg:text-6xl text-ivory">Adam's The Islamic Boutique</h1>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        <div className="text-center">
          <p className="overline mb-4">Est. 1992 · Chennai</p>
          <h2 className="font-serif text-3xl lg:text-5xl text-espresso leading-tight">A Legacy of Modest Fashion Since 1992</h2>
        </div>
        <div className="mt-8 space-y-5 text-espresso/70 leading-relaxed font-sans text-[15px] lg:text-base">
          <p>For over three decades, Adam's The Boutique has been a trusted name in modest fashion, built on a simple philosophy — to bring together elegance, modesty, quality, and contemporary style.</p>
          <p>Founded in 1992, Adam's began its journey with a vision to serve customers looking for modest clothing that reflects both faith and individuality. What started as a humble venture has grown into a well-established boutique with a loyal community of customers who have been part of our journey for generations.</p>
          <p>Today, Adam's The Boutique brings together timeless modest fashion with modern trends. From beautifully crafted abayas, hijabs and jilbabs to accessories and Hajj &amp; Umrah essentials, every collection is thoughtfully selected to offer comfort, quality and effortless elegance.</p>
        </div>

        <h3 className="font-serif text-2xl lg:text-3xl text-espresso mt-12 mb-4">Our Philosophy</h3>
        <div className="space-y-5 text-espresso/70 leading-relaxed font-sans text-[15px] lg:text-base">
          <p>We believe modesty should never mean compromising on style.</p>
          <p>Our collections are designed for the modern woman who values her identity, embraces modesty, and wants to express herself with confidence. We continuously explore new fabrics, silhouettes, colours and styles while staying true to the values that have shaped Adam's since 1992.</p>
        </div>

        <h3 className="font-serif text-2xl lg:text-3xl text-espresso mt-12 mb-4">Three Decades of Trust</h3>
        <div className="space-y-5 text-espresso/70 leading-relaxed font-sans text-[15px] lg:text-base">
          <p>Our greatest achievement is not simply how much we have grown, but the trust our customers have placed in us for more than 30 years.</p>
          <p>From mothers shopping with us years ago to the next generation discovering Adam's today, our journey has always been about people, relationships and trust.</p>
          <p>As we continue to grow, we remain committed to the same values with which we began — quality, authenticity, modesty and exceptional customer experience.</p>
        </div>

        <h3 className="font-serif text-2xl lg:text-3xl text-espresso mt-12 mb-4">Our Journey Continues</h3>
        <div className="space-y-5 text-espresso/70 leading-relaxed font-sans text-[15px] lg:text-base">
          <p>From our roots in 1992 to our growing presence today, Adam's The Boutique continues to evolve with the changing world of modest fashion.</p>
          <p>We invite you to be part of our journey as we continue to redefine modern modest fashion — with heritage at our heart.</p>
        </div>

        <div className="text-center mt-14 pt-10 border-t border-champagne/25">
          {settings.logo_url ? (
            <img src={assetUrl(settings.logo_url)} alt={settings.brand_name} className="h-16 lg:h-20 w-auto object-contain mx-auto" />
          ) : (
            <p className="font-serif text-3xl text-espresso">Adam's The Boutique</p>
          )}
          <p className="overline mt-3">Modesty · Elegance · Since 1992</p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 pb-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { icon: Sparkles, title: "Premium Quality", text: "Only the finest abaya fabrics — Nida crepe, silk, chiffon & Malaysian cotton, tailored to last." },
          { icon: Heart, title: "Designed with Care", text: "Every piece is thoughtfully crafted for comfort, confidence and effortless elegance." },
          { icon: Leaf, title: "Timeless & Ethical", text: "Versatile silhouettes made responsibly — fashion you'll cherish for years." },
        ].map((v) => (
          <div key={v.title} className="bg-beige/50 rounded-3xl p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-champagne/15 flex items-center justify-center mx-auto mb-5"><v.icon className="w-6 h-6 text-champagne" /></div>
            <h3 className="font-serif text-2xl text-espresso">{v.title}</h3>
            <p className="text-espresso/60 mt-3 text-sm leading-relaxed">{v.text}</p>
          </div>
        ))}
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div className="rounded-3xl overflow-hidden aspect-[4/3] hover-zoom"><img src={IMG1} alt="Boutique interior" className="w-full h-full object-cover" /></div>
        <div>
          <p className="overline mb-3">Visit Us</p>
          <h2 className="font-serif text-3xl lg:text-4xl text-espresso">Find Your Nearest Boutique</h2>
          <div className="mt-6 space-y-3">
            {STORES.map((s) => (
              <a key={s.name} href={s.maps} target="_blank" rel="noreferrer" className="flex justify-between items-center border-b border-champagne/20 py-3 group">
                <div><p className="font-serif text-lg text-espresso">{s.name}</p><p className="text-xs text-espresso/50">{s.city} · {s.hours}</p></div>
                <ArrowRight className="w-4 h-4 text-champagne group-hover:translate-x-1 transition-transform" />
              </a>
            ))}
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-8 bg-espresso text-ivory px-8 py-3.5 rounded-full text-sm">Get in Touch <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
}
